import { WidgetSettings } from "assets/icons/Common";
import { Row } from "components/primitives";
import { DashboardContext } from "contexts/DashboardContext";
import { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { WidgetFC } from "types/Widget";

import { saveGroupConfigurationsGloballyGenric } from "./Helper";
import { SavedGroupConfigurations } from "./types";

export const InteractiveGroupMaker: WidgetFC<SavedGroupConfigurations> = ({
  state,
  setState,
  settings,
  onOpenSettings,
}) => {
  const { nameList, groupCount, groupingMode, organise = [] } = settings || {};
  const {
    data: { savedGroupConfigurations },
    setData: setGlobalData,
  } = useContext(DashboardContext);

  const [selectedConfig, setSelectedConfig] = useState<SavedGroupConfigurations>();

  // Function to add points to a group

  const addPoints = (groupIndex: number) => {
    if (selectedConfig?.groups) {
      // Create a deep copy of the configuration and update the points
      const updatedConfig = {
        ...selectedConfig,
        groups: [...selectedConfig.groups],
      };
      updatedConfig.groups[groupIndex].point += 1;

      // Update the local state

      // Update the global state
      saveGroupConfigurationsGloballyGenric(updatedConfig, savedGroupConfigurations, setGlobalData);
    } else {
      console.error("Groups are undefined in the selected configuration");
    }
  };
  // Function to subtract points from a group
  const subtractPoints = (groupIndex: number) => {
    if (selectedConfig?.groups) {
      const updatedConfig = {
        ...selectedConfig,
        groups: [...selectedConfig.groups],
      };

      // Ensure points do not go below zero
      if (updatedConfig.groups[groupIndex].point > 0) {
        updatedConfig.groups[groupIndex].point -= 1;

        // Update the global state
        saveGroupConfigurationsGloballyGenric(
          updatedConfig,
          savedGroupConfigurations,
          setGlobalData,
        );
      } else {
        console.warn("Points cannot be less than zero");
      }
    } else {
      console.error("Groups are undefined in the selected configuration");
    }
  };

  useEffect(() => {
    if (!Array.isArray(settings?.organise)) {
      setSelectedConfig(undefined);
      return;
    }
    const matchedConfig = savedGroupConfigurations?.find((config) =>
      settings?.organise?.some((org: any) => org?.id === config?.id),
    );
    if (matchedConfig) {
      setSelectedConfig({ ...matchedConfig });
    } else {
      setSelectedConfig(undefined);
    }
  }, [settings?.organise, savedGroupConfigurations]);

  return (
    <OuterContainer>
      {selectedConfig && (
        <>
          <ConfigTitleContainer>
            <ConfigName>{selectedConfig?.groupConfigName}</ConfigName>
          </ConfigTitleContainer>

          <GroupsContainer>
            {selectedConfig?.groups?.map((group, groupIndex) => (
              <GroupRow key={groupIndex} bgColor={group.bgColor}>
                <GroupHeaderContainer>
                  <GroupTitleContainer>
                    <GroupTitle value={group?.title} disabled data-hover={group?.title} />
                  </GroupTitleContainer>
                  {Array.isArray(settings?.organise) && settings?.organise[0]?.showPoints && (
                    <GroupPointsContainer>
                      <AdjustPointsContainer>
                        <AdjustButton onClick={() => subtractPoints(groupIndex)}>-</AdjustButton>
                      </AdjustPointsContainer>

                      <PointsWithIconContainer>
                        <PointsIcon>⭐</PointsIcon>
                        <Points>{group?.point} Points</Points>
                      </PointsWithIconContainer>
                      <AdjustPointsContainer>
                        <AdjustButton onClick={() => addPoints(groupIndex)}>+</AdjustButton>
                      </AdjustPointsContainer>
                    </GroupPointsContainer>
                  )}
                </GroupHeaderContainer>

                <GroupSlotElements>
                  {group.students.map((student, studentIndex) => (
                    <GroupSlot key={studentIndex}>
                      <span>{student}</span>
                    </GroupSlot>
                  ))}
                </GroupSlotElements>
              </GroupRow>
            ))}
          </GroupsContainer>
        </>
      )}
      {!selectedConfig?.groups?.length && (
        <NoContentContainer>
          Open
          <TextBtn onClick={onOpenSettings}>
            <WidgetSettings width={"1em"} height={"1em"} /> <b>Settings</b>
          </TextBtn>
          to create , edit and to display groups
        </NoContentContainer>
      )}
    </OuterContainer>
  );
};
const GroupHeaderContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.2rem;
  width: 100%;
`;
const GroupPointsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
  padding: 5px;
  background-color: #ffefd5;
  border: 1.5px dashed #ffa500;
  border-radius: 8px;
  font-size: 14px;
  font-weight: bold;
  color: #ff4500;
  max-width: 158px;
`;

const PointsWithIconContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6cqw;
  justify-content: center;
`;

const AdjustPointsContainer = styled.div`
  display: flex;
`;

const AdjustButton = styled.button`
  background-color: #ffac10;
  color: white;
  border: none;
  cursor: pointer;
  border-radius: 4px;
  height: 24px;
  display: flex;
  width: 24px;
  justify-content: center;
  align-items: center;

  &:hover {
    background-color: #ffb529;
  }
`;

const TextBtn = styled.button`
  border: none;
  box-shadow: none;
  background-color: transparent;
  display: flex;
  color: var(--widget-primary);
  align-items: center;
  padding: 0 0.5cqw;
  gap: 0.5cqw;
`;

const NoContentContainer = styled(Row)`
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  width: 100%;
  font-size: 3cqw;
  margin-top: 13cqh;
  padding: 1.6cqw;
  text-align: center;
  line-height: 1.2;
`;

const OuterContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 1rem;
  gap: 2cqh;
  overflow: auto;
`;

const ConfigTitleContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.6cqh;
  background-color: #f9f9f9;
  border-bottom: 0.8cqh solid #8f84de;
  margin-bottom: 1rem;
  position: sticky;
  top: 0;
  z-index: 1;
`;

const ConfigName = styled.div`
  font-size: 1rem;
  font-weight: bold;
  color: #8f84de;
  text-align: center;
`;
const GroupsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  height: calc(100% - 60px);
  gap: 1rem;
`;

const GroupRow = styled.div<{ bgColor?: string }>`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.2cqh;
  border: 1px solid #ddd;
  border-radius: 0.6cqh;
  background-color: ${({ bgColor }) => bgColor || "#f8f9fa"};
`;

const GroupTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6cqw;
  flex: 1;
`;

const GroupTitle = styled.input`
  font-size: 0.8rem;
  font-weight: bold;
  width: 100%;
  border: none;
  background: transparent;
  text-overflow: ellipsis;

  &:focus {
    outline: none;
  }
`;

const PointsIcon = styled.div`
  font-size: 0.8rem;
  animation: bounce 1.5s infinite;
  color: gold;

  @keyframes bounce {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-0.8cqh);
    }
  }
`;

const Points = styled.span`
  font-size: 0.8rem;
  color: #ff4500;
`;

const GroupSlotElements = styled.div`
  display: grid;
  padding: 0 0 0.8rem;
  gap: 0.6cqw;
`;

const GroupSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0.4cqw 0.8cqw;
  gap: 0.4cqw;
  border: 1px solid #ccc;
  border-radius: 0.4cqh;
  background-color: #f8f9fa;
  font-size: 0.8rem;
  color: #333;
`;
